import { ImageLoader } from './ImageLoader.js';
import { StringUtils } from './../main/ObjectUtils.js';

/** The difference between product material and product finish is that the same finish can be
 * applied to more than one material. An example of a finish id is "BlackAluminum", and an
 * example of a material id is "AluFlatDivider-BlackAluminum". */
export class ProductMaterialLoader {
  constructor(folderRelativePath, getFinishName) {
    this.loadedMaterials = {};
    this.folderRelativePath = folderRelativePath;
    this.imageLoader = new ImageLoader();
    this.getFinishName = getFinishName;
    //console.log('%cCreated ProductMaterialLoader', 'color: blue');
  }

  makeFullRelativeUrl(filename, resolution) {
    return StringUtils.addTrailingSlash(this.folderRelativePath) + resolution + '/' + filename;
  }

  /** Finds the material by its id in the list of all materials (loaded or not). */
  lookupMaterial(allMaterials, id) {
    //return this.allMaterials.find(material => material.id === id);
    return allMaterials[id];
  }

  getLoadedMaterial(materialIdPrefix, finishId, delimiter = '-') {
    const materialId = materialIdPrefix + delimiter + finishId;
    return this.loadedMaterials[materialId];
  }

  extractFinish(materialId) {
    const index = materialId.lastIndexOf('-');
    return index > -1 ? materialId.substring(index + 1) : materialId;
  }

  getMaterialName(material) {
    const finish = this.getFinishName(material.finish);
    return material.productType
      ? `${material.productType} ${finish} (${material.id})`
      : `${finish} (${material.id})`;
  }

  convertToPropName(name) {
    const ndx = name.indexOf('-');
    if (ndx > -1 && ndx < name.length - 1) {
      const ch = name.charAt(ndx + 1);
      const upper = ch.toUpperCase();
      name = name.slice(0, ndx) + upper + name.slice(ndx + 2);
    }
    return name;
  }

  /**
   * Prepares the material for loading
   * @param {object} material
   * @param {string} resolution Indicating which image (low or high resolution) to prepare.
   * @returns {bool} True if the material was prepared now, False if it was already prepared.
   */
  prepareMaterial(material, resolution) {
    let wasPrepared = false;
    if (material.color) {
      material.isColor = true;
      material.finish = this.extractFinish(material.id);
      material.name = this.getMaterialName(material);
      wasPrepared = true;
    }
    if (material.fileName) {
      material.isImage = true;
      let matrImg = material[resolution]; // Gets the object from the material.resolution property
      if (!matrImg) {
        matrImg = material[resolution] = { resolution }; // Creates a new object and assigns it to material.resolution
      }
      const url = this.makeFullRelativeUrl(material.fileName, resolution);
      const isLoadingOrLoaded = matrImg.isLoading || matrImg.isLoaded;
      if (!isLoadingOrLoaded || matrImg.url !== url) {
        material.finish = this.extractFinish(material.id);
        material.name = this.getMaterialName(material);
        matrImg.url = url;
        matrImg.isLoaded = false;
        matrImg.isLoading = false;
        wasPrepared = true;
      } else /* is already loaded and url is the same */ {
        wasPrepared = false;
      }
    }
    this.loadedMaterials[material.id] = material;
    return wasPrepared;
  }

  /**
   * Iterates the material id array and assigns the name and url properties of each material which
   * hasn't already been loaded. Returns an array of material of the ones which need to be loaded.
   * @param {array of material id} materialIds
   * @param {string} resolution Indicates which images to load, low/high resolution etc. The param value
   * should be the name of the folder containing resolution specific images, e.g. 'low-res' or 'high-res'.
   * @return {array of material object} The materials which are being loaded.
   */
  prepareLoading(allMaterials, materialIds, resolution) {
    const loadingImageMaterials = [];
    Object.keys(materialIds).forEach(materialId => {
      /* materialIds is an object where each property is a material id. The value of each property
       * is either, true, false, or undefined. */
      if (materialIds[materialId]) {
        const material = this.lookupMaterial(allMaterials, materialId);
        if (material) {
          if (this.prepareMaterial(material, resolution) && material.isImage) {
            loadingImageMaterials.push(material);
          }
        }
      }
    });
    //console.log(`${loadingMaterials.length} materials prepared for load!`);
    return loadingImageMaterials;
  }

  /**
   * Asynchronously starts loading the images linked to by the url property of each material
   * in the loadingMaterials array. Returns the loadingMaterials array when the entire
   * operation has completed.
   * @param {array of Material} loadingMaterials
   * @param {string} resolution Indicates which images to load, low/high resolution etc. The param value
   * should be the name of the folder containing resolution specific images, e.g. 'low-res' or 'high-res'.
   * @return {array of material object} The materials which are being loaded.
   */
  async startLoading(loadingMaterials, resolution) {
    if (loadingMaterials.length === 0) {
      return loadingMaterials;
    }
    const beforeLoad = (img, material) => {
      const matrImg = material[resolution];
      img.src = matrImg.url;
      matrImg.isLoading = true;
      if (!material.current) {
        // Allow code to access the current material image between now and when the image load completes:
        material.current = matrImg;
      }
    };
    const onLoad = (img, material) => {
      const matrImg = material[resolution];
      matrImg.image = img;
      matrImg.isLoaded = true;
      matrImg.isLoading = false;
      //console.log(`${resolution} image ${material.name} loaded`);
    };
    const onError = (img, material) => {
      material[resolution].isLoading = false;
      console.log(`Unable to load image ${img.src}`);
    };

    //console.log(`Downloading ${loadingMaterials.length} ${resolution} material image(s)...`);
    const imgArray = await this.imageLoader.loadImages(loadingMaterials, beforeLoad, onLoad, onError);
    //console.log(`${imgArray.length} ${resolution} material image(s) downloaded!`);
    return loadingMaterials;
  }

  /**
   * Asynchronously loads the materials referenced in the materialIds array, but only those which
   * have not already been loaded. Returns an array of the materials which just got loaded when the
   * entire operation has completed. To access all loaded materials, including those which were
   * previously loaded, use the loadedMaterials property.
   * @param {string} resolution Indicates which images to load, low/high resolution etc. The param value
   * should be the name of the folder containing resolution specific images, e.g. 'low-res' or 'high-res'.
   * @return {array of material object} The materials which are being loaded.
   */
  async load(allMaterials, materialIds, resolution) {
    const imageMaterialsToBeLoaded = this.prepareLoading(allMaterials, materialIds, resolution);
    return await this.startLoading(imageMaterialsToBeLoaded, resolution);
  }

  /**
   * Asynchronously load a single material, but only if it has not already been loaded.
   * @param {string} resolution Indicates which images to load, low/high resolution etc. The param value
   * should be the name of the folder containing resolution specific images, e.g. 'low-res' or 'high-res'.
   * @return {bool} True if the material was loaded now, false if it was already loaded (loaded earlier).
   */
  async loadSingleMaterial(material, resolution) {
    if (this.prepareMaterial(material, resolution)) {
      if (material.isImage) {
        const materials = [material];
        await this.startLoading(materials, resolution);
      }
      return true;
    }
    return false;
  }
}
